/* eslint-disable promise/catch-or-return */

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getReportConfig } from 'reporting-data/report/configReportGetters';
import { isUnifiedAnalyticsReport } from 'reporting-data/tsTypes/reportTypes';
import { fromMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import { DateActionTypes } from 'reporting-ui-components/constants/trackingConstants';
import { EVENTS } from 'reporting-ui-components/lib/events';
import { getUsageTracker } from 'reporting-ui-components/monitoring/usageTracker';
import { getMetricOptions } from '../../../lib/metricUtils';
import { isNumericProperty } from '../MetricEditor/metricEditorUtils';
import BaseEditTableMetricsModal from './BaseTableMetricEditorModal';
import { getMetricKeysFromReportConfig } from './tableMetricEditorUtils';

// @ts-expect-error need to add types
import getProperties from 'reporting-data/properties';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const filterMetricKeysByIsNumeric = (metricKeys, propertiesByName, isNumericTarget) => {
  if (Object.keys(propertiesByName).length === 0) {
    return [];
  }
  return metricKeys.filter(metricKey => {
    const {
      property
    } = fromMetricKey(metricKey);
    const propertyData = propertiesByName[property];
    const isNumeric = propertyData && isNumericProperty(propertyData);
    return isNumeric === isNumericTarget;
  });
};
const BaseTableMetricsEditor = props => {
  const [propertiesByName, setPropertiesByName] = useState({});
  const {
    isV2FilterExperience,
    report,
    strategy,
    schema: editorSchema,
    trackingApplicationName,
    trackingAssetType,
    trackingScreenName,
    onChange
  } = props;
  const usageTracker = getUsageTracker();
  const {
    getDataType,
    getCurrentValue,
    updateReport
  } = strategy;
  const [modalOpen, setModalOpen] = useState(false);
  const dataType = getDataType(report);
  const isUnifiedAnalytics = isUnifiedAnalyticsReport(report);
  const reportConfig = getReportConfig(report);
  const metricKeysFromReportConfig = getMetricKeysFromReportConfig(reportConfig);
  useEffect(() => {
    getProperties(dataType).then(dataTypeProperties => {
      const properties = dataTypeProperties.get(dataType);
      setPropertiesByName(properties ? properties.toJS() : {});
    });
  }, [dataType]);
  const metricProperties = useMemo(() => filterMetricKeysByIsNumeric(editorSchema.metrics, propertiesByName, true), [editorSchema.metrics, propertiesByName]);
  const nonMetricProperties = useMemo(() => {
    if (editorSchema.tableOnlyMetrics) {
      return editorSchema.tableOnlyMetrics;
    }
    return filterMetricKeysByIsNumeric(editorSchema.metrics, propertiesByName, false);
  }, [editorSchema.metrics, editorSchema.tableOnlyMetrics, propertiesByName]);
  const options = useMemo(() => [{
    text: I18n.text('reporting-enablement.reportEditors.tableMetrics.groups.metrics'),
    options: getMetricOptions(metricProperties, propertiesByName, metricKeysFromReportConfig, isUnifiedAnalytics)
  }, {
    text: I18n.text('reporting-enablement.reportEditors.tableMetrics.groups.tableOnlyMetrics'),
    options: getMetricOptions(nonMetricProperties, propertiesByName, metricKeysFromReportConfig, isUnifiedAnalytics)
  }], [isUnifiedAnalytics, metricKeysFromReportConfig, metricProperties, nonMetricProperties, propertiesByName]);
  const handleChange = useCallback(newSelectedMetrics => {
    const updatedReport = updateReport(newSelectedMetrics.toArray(), report);
    usageTracker.track(EVENTS.changeSrvDateFilters, {
      action: DateActionTypes.TABLE_METRIC,
      assetType: trackingAssetType || 'report',
      whereScreen: trackingScreenName,
      applicationName: trackingApplicationName,
      isNewFilterExperience: isV2FilterExperience
    });
    onChange === null || onChange === void 0 || onChange(updatedReport);
  }, [isV2FilterExperience, report, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker, onChange, updateReport]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [modalOpen && /*#__PURE__*/_jsx(BaseEditTableMetricsModal, {
      closeModal: () => {
        setModalOpen(false);
      },
      onSubmit: handleChange,
      options: options,
      selectedMetrics: getCurrentValue(report)
    }), /*#__PURE__*/_jsx(Button, {
      use: "link",
      onClick: () => {
        setModalOpen(true);
      },
      "data-test-id": "report-editor-table-metric-editor-open-modal",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-enablement.reportEditors.tableMetrics.label"
      })
    })]
  });
};
export default BaseTableMetricsEditor;